import { Theme, createTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import mobileTableImage from '/images/table/mobile-game-table.svg';
import mobileTableImageAof from '/images/table/mobile-game-table-aof.svg';
import desktopTableImage from '/images/table/desktop-game-table.svg';
import desktopTableImageAof from '/images/table/desktop-game-table-aof.svg';
import { isMobile } from 'react-device-detect';


declare module '@mui/material/styles' {
  interface Theme {
    poker: {
      tableBg: string;
      tableBorder: string;
      tableBoxShadow: string;
      potInfoBg: string;
      potInfoColor: string;
      potValueColor: string;
      cardBg: string;
      playerSlotBg: string;
      playerNameColor: string;
      playerBalanceColor: string;
      actionBadgeBg: string;
      actionBadgeColor: string;
      roundNameBg: string;
      roundNameColor: string;
      sitHereTextColor: string;
      activityMessagesBg: string;
      activityMessagesColor: string;
      playerTurnHistoryBg: string;
      playerTurnHistoryColor: string;
      tableDataBg: string;
      tableDataColor: string;
      tableAspectRatio?: string;
      tableWidth?: string;
      tableHeight?: string;
    };
  }
  interface ThemeOptions {
    poker?: {
      tableBg?: string;
      tableBorder?: string;
      tableBoxShadow?: string;
      potInfoBg?: string;
      potInfoColor?: string;
      potValueColor?: string;
      cardBg?: string;
      playerSlotBg?: string;
      playerNameColor?: string;
      playerBalanceColor?: string;
      actionBadgeBg?: string;
      actionBadgeColor?: string;
      roundNameBg?: string;
      roundNameColor?: string;
      sitHereTextColor?: string;
      activityMessagesBg?: string;
      activityMessagesColor?: string;
      playerTurnHistoryBg?: string;
      playerTurnHistoryColor?: string;
      tableDataBg?: string;
      tableDataColor?: string;
      tableAspectRatio?: string;
      tableWidth?: string;
      tableHeight?: string;
    };
  }
}

const baseTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
        main: "#F93751"
    }
  },
  typography: {
      fontFamily: 'Inter, serif',
  },
});

const createPokerTheme = (pokerProps: Theme['poker']) => {
  return createTheme(deepmerge(baseTheme, { poker: pokerProps }));
};

export const classicTheme = createPokerTheme({
  tableBg: `url(${isMobile ? mobileTableImage : desktopTableImage})`,
  tableBorder: 'none',
  tableBoxShadow: 'none',
  potInfoBg: 'rgba(0, 0, 0, 0.6)',
  potInfoColor: 'white',
  potValueColor: '#3AD65D',
  cardBg: '#FFFFFF',
  playerSlotBg: 'rgba(20, 21, 26, 0.9)',
  playerNameColor: '#FFD700',
  playerBalanceColor: '#FFFFFF',
  actionBadgeBg: 'rgba(0, 0, 0, 0.8)',
  actionBadgeColor: '#FFFFFF',
  roundNameBg: 'rgba(0, 0, 0, 0.6)',
  roundNameColor: 'white',
  sitHereTextColor: '#FFD700',
  activityMessagesBg: '#24242F',
  activityMessagesColor: 'white',
  playerTurnHistoryBg: '#24242F',
  playerTurnHistoryColor: 'white',
  tableDataBg: '#24242F',
  tableDataColor: 'white',
  tableAspectRatio: isMobile ? '97 / 137' : '300 / 127',
  tableWidth: isMobile? '100%' : 'auto',
  tableHeight: isMobile? 'auto' : '100%'
});

export const modernTheme = createPokerTheme({
  tableBg: `url(${isMobile ? mobileTableImageAof : desktopTableImageAof})`,
  tableBorder: 'none',
  tableBoxShadow: 'none',
  potInfoBg: 'rgba(0, 0, 0, 0.6)',
  potInfoColor: 'white',
  potValueColor: '#3AD65D',
  cardBg: '#FFFFFF',
  playerSlotBg: 'rgba(61, 62, 68, 0.9)',
  playerNameColor: '#FFD700',
  playerBalanceColor: '#FFFFFF',
  actionBadgeBg: 'rgba(0, 0, 0, 0.8)',
  actionBadgeColor: '#FFFFFF',
  roundNameBg: 'rgba(0, 0, 0, 0.6)',
  roundNameColor: 'white',
  sitHereTextColor: '#FFD700',
  activityMessagesBg: '#24242F',
  activityMessagesColor: 'white',
  playerTurnHistoryBg: '#24242F',
  playerTurnHistoryColor: 'white',
  tableDataBg: '#24242F',
  tableDataColor: 'white',
  tableAspectRatio: isMobile ? '97 / 137' : '300 / 127',
  tableWidth: isMobile? '100%' : 'auto',
  tableHeight: isMobile? 'auto' : '100%'
});

export const luxuryTheme = createPokerTheme({
  tableBg: `url(${'foo'})`,
  tableBorder: '15px solid #B8860B',
  tableBoxShadow: '0 0 0 2px #FFD700, inset 0 0 0 2px #FFD700',
  potInfoBg: 'rgba(184, 134, 11, 0.8)',
  potInfoColor: '#FFFFFF',
  potValueColor: '#FFD700',
  cardBg: '#FFFAFA',
  playerSlotBg: 'rgba(139, 69, 19, 0.8)',
  playerNameColor: '#FFD700',
  playerBalanceColor: '#FFFFFF',
  actionBadgeBg: 'rgba(184, 134, 11, 0.9)',
  actionBadgeColor: '#FFFFFF',
  roundNameBg: 'rgba(184, 134, 11, 0.8)',
  roundNameColor: 'white',
  sitHereTextColor: '#FFD700',
  activityMessagesBg: 'black',
  activityMessagesColor: 'white',
  playerTurnHistoryBg: 'gray',
  playerTurnHistoryColor: 'white',
  tableDataBg: 'white',
  tableDataColor: 'black',
  tableAspectRatio: '1243 / 650',
  tableWidth: '1300px',
  tableHeight: '100%'
});

export const westernTheme = createPokerTheme({
  tableBg: `url(${'foo'})`,
  tableBorder: '15px solid #8B4513',
  tableBoxShadow: '0 0 0 2px #D2691E, inset 0 0 0 2px #D2691E',
  potInfoBg: 'rgba(139, 69, 19, 0.8)',
  potInfoColor: '#F4A460',
  potValueColor: '#FFD700',
  cardBg: '#FAEBD7',
  playerSlotBg: 'rgba(139, 69, 19, 0.8)',
  playerNameColor: '#F4A460',
  playerBalanceColor: '#FFDAB9',
  actionBadgeBg: 'rgba(139, 69, 19, 0.9)',
  actionBadgeColor: '#FFDAB9',
  roundNameBg: 'rgba(139, 69, 19, 0.8)',
  roundNameColor: 'white',
  sitHereTextColor: '#F4A460',
  activityMessagesBg: 'black',
  activityMessagesColor: 'white',
  playerTurnHistoryBg: 'gray',
  playerTurnHistoryColor: 'white',
  tableDataBg: 'white',
  tableDataColor: 'black',
  tableAspectRatio: '1243 / 650',
  tableWidth: '1300px',
  tableHeight: '100%'
});

export const spaceTheme = createPokerTheme({
  tableBg: `url(${'foo'})`,
  tableBorder: '15px solid #4B0082',
  tableBoxShadow: '0 0 0 2px #8A2BE2, inset 0 0 0 2px #8A2BE2',
  potInfoBg: 'rgba(75, 0, 130, 0.8)',
  potInfoColor: '#E6E6FA',
  potValueColor: '#7FFF00',
  cardBg: '#E6E6FA',
  playerSlotBg: 'rgba(75, 0, 130, 0.8)',
  playerNameColor: '#00FFFF',
  playerBalanceColor: '#E6E6FA',
  actionBadgeBg: 'rgba(75, 0, 130, 0.9)',
  actionBadgeColor: '#E6E6FA',
  roundNameBg: 'rgba(75, 0, 130, 0.8)',
  roundNameColor: 'white',
  sitHereTextColor: '#00FFFF',
  activityMessagesBg: 'black',
  activityMessagesColor: 'white',
  playerTurnHistoryBg: 'gray',
  playerTurnHistoryColor: 'white',
  tableDataBg: 'white',
  tableDataColor: 'black',
  tableAspectRatio: '1243 / 650',
  tableWidth: '1300px',
  tableHeight: '100%'
});

export type PokerTheme = typeof classicTheme;
